import React, { useEffect, useState } from 'react';
import { api } from '../../App';

const MODE = {
  live: { label: 'Live', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: '#16A34A' },
  simulated: { label: 'Simulated', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: '#F59E0B' },
  fallback: { label: 'Fallback model', cls: 'bg-sky-50 text-sky-700 border-sky-200', dot: '#21A84A' },
  disabled: { label: 'Off — needs key', cls: 'bg-slate-100 text-slate-600 border-slate-200', dot: '#94A3B8' },
};

function VerifyBadge({ r }) {
  if (!r) return null;
  const ok = r.ok;
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${ok ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-rose-50 text-rose-700 border-rose-200'}`} title={r.detail}>
      <i className={`fa-solid ${ok ? 'fa-circle-check' : 'fa-triangle-exclamation'} mr-1`} />{ok ? 'Verified' : 'Failing'}
    </span>
  );
}

export default function IntegrationHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [probe, setProbe] = useState(null);
  const [ai, setAi] = useState(null);
  const [testing, setTesting] = useState(false);
  const [health, setHealth] = useState(null);
  const [hchecking, setHchecking] = useState(false);
  useEffect(() => {
    api.get('/api/system/integrations').then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || 'Failed to load'));
    api.get('/api/system/ai-engine').then(r => setAi(r.data)).catch(() => {});
    api.get('/api/system/key-health').then(r => setHealth(r.data)).catch(() => {});
  }, []);
  const runHealth = async () => {
    setHchecking(true);
    try { const r = await api.post('/api/system/key-health/run'); setHealth({ ...r.data, never_run: false }); } catch (e) { /* surfaced by badge */ }
    setHchecking(false);
  };
  const runProbe = async () => {
    setTesting(true);
    try { const r = await api.post('/api/system/integrations/verify'); setProbe(r.data); } catch (e) { setErr(e.response?.data?.detail || 'Probe failed'); }
    setTesting(false);
  };
  if (err) return <div className="card p-6 text-sm text-destructive" data-testid="integration-health-error">{err}</div>;
  if (!data) return <div className="card p-6 text-sm text-text-muted">Loading integration health…</div>;
  const groups = [...new Set(data.integrations.map(i => i.group))];
  const pct = Math.round((data.live / data.total) * 100);
  return (
    <div className="space-y-5 max-w-4xl" data-testid="integration-health">
      <div className="card p-6 text-white" style={{ background: '#0C2417' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em]" style={{ color: '#FAAB1C' }}>INTEGRATION HEALTH</p>
            <h3 className="font-heading text-2xl font-black mt-1">{data.live} of {data.total} integrations live</h3>
            <p className="text-xs text-slate-300 mt-1">Keys are stored server-side only and never shown here. Paste a key in <b>Key Manager</b> or <code className="font-mono">backend/.env</code> and it flips live — zero code changes.</p>
          </div>
          <div className="text-right"><p className="font-heading font-black text-5xl" data-testid="integration-live-pct">{pct}%</p><p className="text-[10px] text-slate-400 uppercase tracking-widest">live coverage</p></div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#FAAB1C,#21A84A)' }} /></div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button onClick={runProbe} disabled={testing} className="px-4 py-2 rounded-full text-sm font-bold disabled:opacity-60" style={{ background: '#FAAB1C', color: '#0C2417' }} data-testid="verify-integrations-btn">
            {testing ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Testing live connections…</> : <><i className="fa-solid fa-plug-circle-bolt mr-2" />Test live connections</>}
          </button>
          {probe && <p className="text-xs text-slate-300" data-testid="verify-summary">{probe.verified} of {probe.total} data providers responded with a valid key</p>}
        </div>
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-3 flex-wrap" data-testid="key-health-panel">
          <span className="text-[10px] font-bold tracking-widest text-slate-400">NIGHTLY HEALTH CHECK · 5:30 AM UTC</span>
          {health && !health.never_run ? (
            <span className="text-xs text-slate-300" data-testid="key-health-last">
              Last run {new Date(health.checked_at).toLocaleString()} — {health.live}/{health.total} live
              {health.degraded?.length ? <span className="text-amber-300 font-semibold"> · {health.degraded.length} degraded</span> : <span className="text-emerald-300 font-semibold"> · all healthy</span>}
            </span>
          ) : <span className="text-xs text-slate-400">Not run yet</span>}
          <button onClick={runHealth} disabled={hchecking} className="px-3 py-1.5 rounded-full text-xs font-bold border border-white/25 text-white disabled:opacity-60" data-testid="run-key-health-btn">
            {hchecking ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Checking…</> : <><i className="fa-solid fa-heart-pulse mr-1.5" />Run check now</>}
          </button>
        </div>
      </div>
      {ai && (
        <div className="card p-5 border-l-4" style={{ borderLeftColor: '#FAAB1C' }} data-testid="ai-engine-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">AI engine in use</p>
              <h4 className="font-heading font-black text-lg text-text-primary mt-0.5" data-testid="ai-engine-active">{ai.active_engine || 'No AI engine configured'}</h4>
              <p className="text-[11px] text-text-secondary mt-1">Every AI feature (assistant, roof vision, bill parsing, HD renders) routes through this engine. Add your own ChatGPT, Gemini or Claude key in <b>Key Manager</b> to switch — priority: ChatGPT → Gemini → Claude → Phallox.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[['platform', 'Phallox'], ['openai', 'ChatGPT'], ['gemini', 'Gemini'], ['anthropic', 'Claude']].map(([k, l]) => (
                <span key={k} className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${ai.providers?.[k] ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`} data-testid={`ai-provider-${k}`}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ background: ai.providers?.[k] ? '#16A34A' : '#94A3B8' }} />{l}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {groups.map(g => (
        <div key={g} className="card p-5" data-testid={`integration-group-${g.toLowerCase().replace(/ /g, '-')}`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-3">{g}</p>
          <div className="grid md:grid-cols-2 gap-2">
            {data.integrations.filter(i => i.group === g).map(i => {
              const m = MODE[i.mode] || MODE.disabled;
              const r = probe?.results?.[i.key];
              return (
                <div key={i.key} className="flex items-start gap-3 p-3 rounded-xl border border-border bg-surface-alt/50" data-testid={`integration-${i.key}`}>
                  <span className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: r ? (r.ok ? '#16A34A' : '#E11D48') : m.dot }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold text-text-primary truncate">{i.label}</p><div className="flex items-center gap-1.5"><VerifyBadge r={r} /><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${m.cls}`}>{m.label}</span></div></div>
                    <p className="text-[11px] text-text-secondary mt-0.5">{i.unlocks}</p>
                    {r && !r.ok && <p className="text-[11px] text-rose-600 mt-1" data-testid={`integration-${i.key}-issue`}>{r.detail}</p>}
                    <p className="text-[10px] text-text-muted font-mono mt-1">{i.env}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
